import React from 'react'
import {useSelector} from 'react-redux'
import useParams from 'components/data/URLParams/useParams'
import {
  getAnonymousId,
  getIsRtlLanguage,
  getUserCountryWithFallback
} from 'modules/meta/selectors'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {
  getUser,
  selectAccountUrl,
  selectIsAuthenticated,
  selectIsLoading
} from '@vio/core/src/auth/modules/selectors'
import {
  AuthenticationFlows,
  ShowPasswordlessLoginPayload,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import {getMyBookingsUrl} from '@vio/core/src/brand/utils'
import {useLogIn} from '@vio/shared/src/authentication/hooks/useLogIn'
import {useLogOut} from '@vio/shared/src/authentication/hooks/useLogOut'
import {HeaderUserWidget} from '@vio/shared/src/user/account/HeaderUserWidget'

interface UserWidgetWithStoreProps {
  hideMyBookings?: boolean
  hideManageAccount?: boolean
  referralComponent?: SourceComponentType
}

const UserWidgetWithStore = ({
  hideMyBookings = false,
  hideManageAccount = false,
  referralComponent
}: UserWidgetWithStoreProps) => {
  const accountUrl = useSelector(selectAccountUrl)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isLoading = useSelector(selectIsLoading)
  const logIn = useLogIn()
  const logOut = useLogOut()
  const user = useSelector(getUser)
  const {brand} = useBrand()
  const {
    bookings: {hasMyBookings}
  } = useBrandCapabilities()
  const userCountry = useSelector(getUserCountryWithFallback)
  const isRtl = useSelector(getIsRtlLanguage)

  const urlParams = useParams()

  const {locale, currency, utm_source} = urlParams

  const isKayakTraffic = utm_source === 'kayak'

  const anonymousId = useSelector(getAnonymousId)

  const myBookingsUrl =
    hideMyBookings || !hasMyBookings
      ? ''
      : getMyBookingsUrl({
          brand,
          appEnv: process.env.NEXT_PUBLIC_ENV || '',
          anonymousId: anonymousId ?? undefined,
          userCountry,
          locale,
          currency
        })

  if (isLoading) return null

  const logInWithTracking = ({
    sourceComponent,
    flow
  }: {
    sourceComponent: ShowPasswordlessLoginPayload['sourceComponent']
    flow: AuthenticationFlows
  }) => {
    if (anonymousId) {
      setTimeout(
        () =>
          logIn({
            sourceComponent: referralComponent
              ? referralComponent
              : sourceComponent,
            anonymousId,
            flow
          }),
        TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
      )
    }
  }

  const handleLogOutClick = () => {
    if (logOut) logOut()
  }

  return (
    <HeaderUserWidget
      isAuthenticated={Boolean(isAuthenticated)}
      user={user}
      logOut={handleLogOutClick}
      accountUrl={hideManageAccount ? '' : accountUrl}
      myBookingsUrl={myBookingsUrl}
      onSignIn={logInWithTracking}
      isSignInHidden={isKayakTraffic}
      isRtl={isRtl}
    />
  )
}

export default UserWidgetWithStore
